import React, { useContext, useEffect, useState } from 'react';
import moment from 'moment/moment';
import classNames from 'classnames';
import {Link, useMatch, useNavigate, useOutletContext} from 'react-router-dom';
// @ts-ignore
import permissions from 'invitemember-permissions-model';
import StaticLayoutInner from '@/layouts/StaticLayoutInner';
import { OutletContext, PlanType, Resource } from '@/types';
import { useFetch } from '@/hooks/useFetch';
import { LoadingScreen } from '@/components/LoadingScreen';
import { fetchAPI } from '@/api';
import { humanizeDuration } from '@/utils/durations';
import BaseDynamicAPITable from '@/components/Tables/BaseDynamicAPITable';
import ThreeDotsVertical from '@/images/icons/ThreeDotsVertical';
import Dropdown from '@/components/Dropdown';
import { DropdownContext } from '@/components/Dropdown/DropdownContext';
import { useHasPermission } from '@/hooks/useHasPermission';
import EditPlanModal from '@/pages/settings/SubscriptionPlans/EditPlanModal';
import ErrorModal from '@/components/ErrorModal';
import BackButtonPage from '@/components/BackButtonPage';
import EyeCrossedCircle from '@/images/icons/EyeCrossedCircle';
import NewPlanModal from '@/pages/settings/SubscriptionPlans/NewPlanModal';
import DeletePlanModal from '@/pages/settings/DeletePlanModal';
import { priceView } from '@/utils/utils';

function SubscriptionPlan() {
  const { project, auth } = useOutletContext<OutletContext>();
  const navigate = useNavigate();
  const match = useMatch("/settings/subscription-plans/:planId");
  const { data, isLoading: isPlanLoading, callFetch }: any = useFetch('/project/plan', {
    projectId: project.projectId,
    planId: match?.params.planId
  });
  const { openDropdownId } = useContext(DropdownContext);
  const isDropdownOpen = openDropdownId === 'toggle-dropdown';


  const hasPlanCreatePermissions = useHasPermission(permissions.CREATE_SUBSCRIPTION_PLANS);
  const hasPlanEditPermissions = useHasPermission(permissions.EDIT_SUBSCRIPTION_PLANS);
  const hasPlanDeletePermissions = useHasPermission(permissions.DELETE_SUBSCRIPTION_PLANS);

  const [plan, setPlan] = useState<any>(data?.plan);
  const [isLoading, setIsLoading] = useState<boolean>(isPlanLoading);
  const [editPlanModalOpen, setEditPlanModalOpen] = useState<boolean>(false);

  const [errorModal, setErrorModal] = useState<null | string>(null);
  const [errorModalOpen, setErrorModalOpen] = useState(false);

  const [newPlanModalOpen, setNewPlanModalOpen] = useState(false);
  const [isNewPlanLoading, setIsNewPlanLoading] = useState(false);

  const [deletePlanModalOpen, setDeletePlanModalOpen] = useState(false);

  const [planResources, setPlanResources] = useState<any[]>([]);
  const [isResourcesLoading, setIsResourcesLoading] = useState<boolean>(true);

  useEffect(() => {
      callFetch();
  }, []);

  useEffect(() => {
    if (data?.plan) {
      setPlan(data?.plan);
    }
  }, [data]);

  useEffect(() => {
    if (plan) {
      const filteredResources = plan.resources.filter((resource: Resource) =>
        plan.resourceIds && plan.resourceIds.includes(resource.resourceId)
      );
      setPlanResources(filteredResources);
    }
  }, [plan?.resourceIds]);

  useEffect(() => {
    if (plan?.planId) {
      fetchPlanResources();
    }
  }, [plan?.planId]);

  useEffect(() => {
    setIsLoading(isPlanLoading);
  }, [isPlanLoading]);

  useEffect(() => {
    if (errorModal) {
      setEditPlanModalOpen(false);
      setErrorModalOpen(true);
    }
  }, [errorModal]);

  useEffect(() => {
    if (!errorModalOpen) {
      setErrorModal(null);
    }
  }, [errorModalOpen]);

  console.log("project", project);

  function getPlanItems(plan: PlanType) {
    return [
      plan?.descriptionMessage?.text && ["Description", plan.descriptionMessage.text],
      plan?.price && [
        "Price",
        plan.price.amount === 0
          ? 'FREE'
          : `${priceView(plan.price.amount, plan.price.currency)} ${plan.price.currency === 'XTR' ? '⭐' : plan.price.currency}`
      ],
      plan?.period && ["Billing period", humanizeDuration(plan.period)],
      plan?.isNonRecurring !== undefined && ["Recurring plan", plan.isNonRecurring ? 'OFF' : 'ON'],
      (project && plan?.planId) && ["Direct link", <span><Link to={"https://im.page/" + project.pageBasePath + "/plan?planId=" + plan?.planId}>Page</Link>{project.telegramBotUsername && <span>&nbsp;/&nbsp;<Link className={""} to={"https://t.me/" + project.telegramBotUsername + "?start=p" + plan.planId}>Bot</Link></span>}</span>],
      plan?.trialPeriodDays && ["Trial period", `${plan.trialPeriodDays} days`],
      plan?.trialPeriodWithoutCard && ["Trial period without card", "ON"],
      plan?.isTrialOncePerPlan !== undefined && plan?.trialPeriodDays && plan?.trialPeriodDays > 0 && ["Trial mode", plan.isTrialOncePerPlan ? "Once per plan" : "Once per project"],
      plan?.isNewcomersOnly && ["Newcomers only", "ON"],
      plan?.isNonNewcomersOnly && ["Customers only", "ON"],
      plan?.isChurnedOnly && ["Churned only", "ON"],
      plan?.isSingleUse && ["Single use only", "ON"],
      plan?.isPayAccessCodeOnly && ["Pay with Access Code only", "ON"],
      plan?.allowHiddenRenewal && ["Allow disabled plan renewals", "ON"],
    ].filter(Boolean);
  }

  const wrapperClass = "flex";
  const titleClass = "text-[11px] sm:text-[13px]/[1.2] text-[#A5A5A5] font-medium";
  const contentClass = "text-[11px] sm:text-[13px]/[1.2] sm:max-w-[34ch] sm:truncate";

  const items = getPlanItems(plan);
  const allTitles = items.map(([title]) => title);
  const maxLength = allTitles.reduce((acc, curr) => Math.max(acc, curr.length), 0);
  const estimatedWidth = `${maxLength / 1.8}em`;

  const chunkedItems = [];
  for (let i = 0; i < items.length; i += 3) {
    const chunk = items.slice(i, i + 3);

    chunkedItems.push(
      <div key={`chunk-${i}`} className="flex flex-col gap-y-4">
        {chunk.map(([title, content], idx) => (
          <div className={wrapperClass} key={idx}>
            <div
              className={titleClass}
              style={{ width: estimatedWidth, minWidth: estimatedWidth, whiteSpace: "nowrap" }}
            >
              {title}
            </div>
            <div className={contentClass}>{content}</div>
          </div>
        ))}
      </div>
    );
  }

  const fetchPlanResources = async () => {
    setIsResourcesLoading(true);
    try {
      const response = await fetchAPI("/project/plan/resources", auth.user.token, {
        projectId: project.projectId,
        planId: plan.planId,
      });
      const result = await response.json();

      if (response.ok && result?.length > 0) {
        const filteredResources = result.filter((resource: Resource) =>
          plan.resourceIds && plan.resourceIds.includes(resource.resourceId)
        );
        setPlanResources(filteredResources);
      } else {
        setErrorModal("Failed to fetch plan resources");
      }
    } catch (error: any) {
      setErrorModal(error.message || "Failed to fetch plan resources");
    } finally {
      setIsResourcesLoading(false);
    }
  };

  const onTogglePlan = async () => {
    if (plan.isHidden) {
      onPlanUpdate({ isHidden: false });
    } else {
      onPlanUpdate({ isHidden: true });
    }
  }

  const onDuplicatePlan = () => {
    const resourceIds = planResources.map((resource) => resource.resourceId);

    const planWithResources = {
      ...plan,
      resourceIds
    };

    setPlan(planWithResources);
    setNewPlanModalOpen(true);
  };

  const onPlanCreate = async (plan: Partial<PlanType>) => {
    setIsNewPlanLoading(true);
    try {
      const response = await fetchAPI("/plan/create", auth.user.token, {
        projectId: project.projectId,
        ...plan,
      });
      const result = await response.json();

      if (result.plan) {
        setNewPlanModalOpen(false);
        setTimeout(() => {
          navigate('/settings/subscription-plans');
        }, 500);
      }
    } catch (error: any) {
      console.log("Error updating project:", error.message);
      setErrorModal(error.message || "Failed to create a plan");
    } finally {
      setIsNewPlanLoading(false);
    }
  };

  const onDeletePlan = async () => {
    setIsLoading(true);
    try {
      const response = await fetchAPI("/plan/delete", auth.user.token, {
        projectId: project.projectId,
        planId: plan.planId,
      });
      const result = await response.json();

      if (response.ok && result) {
        navigate('/settings/subscription-plans');
      } else {
        setErrorModal(result.message || "Failed to delete the plan.");
      }
    } catch (error: any) {
      setErrorModal(error.message);
      console.log("Error updating project:", error.message);
    } finally {
      setIsLoading(false);
    }
  };

  const onPlanUpdate = async (updatedPlan: Partial<PlanType>) => {
    setIsLoading(true);

    try {
      const response = await fetchAPI("/plan/update", auth.user.token, {
        projectId: project.projectId,
        planId: plan.planId,
        ...updatedPlan,
      });
      const result = await response.json();

      if (response.ok && result.plan) {
        setPlan(result.plan);
      } else {
        setErrorModal(result.message || "Failed to update the plan.");
      }
    } catch (error: any) {
      setErrorModal(error.message);
      setEditPlanModalOpen(false);
      console.log("Error updating project:", error.message);
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <>
      {(isLoading || !data) ? <LoadingScreen /> : (
        <StaticLayoutInner>
          {plan && (
            <>
              <div className="relative">
                <BackButtonPage to={`/settings/subscription-plans`} title="Subscriptions" />
                <div className="flex flex-col w-full sm:grid sm:grid-cols-[3fr_1fr] xxl:grid-cols-[2fr_1fr] mb-8 sm:mb-12 relative">
                  <div className="bg-white border border-[#F2F2F2] rounded-xl py-4 md:py-4 px-4 md:px-5 flex-grow flex flex-col items-start">
                    <h1 className="h2 mb-5">
                      <div>{plan.name} {plan.isHidden && <EyeCrossedCircle className="inline w-6 h-6 ml-2" backgroundColor="#F2F2F2" />}</div>
                    </h1>
                    <div className="w-full mt-3 mb-1">
                      <div className="grid xl:grid-cols-2 gap-x-1 gap-y-4">
                        {chunkedItems}
                      </div>
                    </div>
                  </div>

                  <div className="flex justify-end gap-2.5 mt-4 sm:mt-1">
                    {hasPlanEditPermissions && (
                      <button className="btn-secondary" onClick={() => setEditPlanModalOpen(true)}>
                        <span>Edit</span>
                        <svg className="ml-1 w-5 h-5"
                             width="20"
                             height="20"
                             viewBox="0 0 20 20"
                             fill="none"
                             xmlns="http://www.w3.org/2000/svg">
                          <path d="M13.5351 3.63679C13.9256 3.24627 14.5588 3.24627 14.9493 3.63679L16.3635 5.05101C16.754 5.44153 16.754 6.0747 16.3635 6.46522L14.9493 7.87943L12.1209 5.05101L13.5351 3.63679Z"
                                fill="currentColor" />
                          <path d="M11.4141 5.75781L14.2425 8.58624L7.17142 15.6573H4.34299V12.8289L11.4141 5.75781Z"
                                fill="currentColor" />
                        </svg>
                      </button>
                    )}
                    {(hasPlanEditPermissions || hasPlanDeletePermissions) && (
                      <Dropdown
                        id="toggle-dropdown"
                        button={
                          <button className={classNames(
                            'btn-secondary w-8 h-8 outline-0',
                            isDropdownOpen && 'bg-[#DFD8CD] border-[#DFD8CD]',
                          )}>
                            <ThreeDotsVertical />
                          </button>
                        }
                        className="absolute top-3 sm:top-0 right-3 sm:right-0 sm:relative inline-flex self-start"
                        top="top-[calc(100%+10px)]"
                        minWidth="min-w-40"
                      >
                        {hasPlanEditPermissions && (
                          <button
                            className="flex items-center py-2.5 px-4 w-full text-white text-sm font-medium border-b border-b-[#313334] last:border-b-0 first:rounded-t-xl last:rounded-b-xl hover:bg-[#313334] transition"
                            onClick={() => onTogglePlan()}
                          >
                            {plan.isHidden ? 'Enable' : 'Disable'}
                          </button>
                        )}
                        {hasPlanCreatePermissions && (
                          <button
                            className="flex items-center py-2.5 px-4 w-full text-white text-sm font-medium border-b border-b-[#313334] last:border-b-0 first:rounded-t-xl last:rounded-b-xl hover:bg-[#313334] transition"
                            onClick={() => onDuplicatePlan()}
                          >
                            Duplicate
                          </button>
                        )}
                        {hasPlanDeletePermissions && (
                          <button
                            className="flex items-center py-2.5 px-4 w-full text-white text-sm font-medium border-b border-b-[#313334] last:border-b-0 first:rounded-t-xl last:rounded-b-xl hover:bg-[#313334] transition"
                            onClick={() => setDeletePlanModalOpen(true)}
                          >
                            Delete
                          </button>
                        )}
                      </Dropdown>
                    )}
                  </div>
                </div>
              </div>
              <div className="mt-10 md:mt-14">
                <h2 className="h1 md:h3 mb-4">Benefits</h2>
                <BaseDynamicAPITable
                  preFetchedData={planResources}
                  preFetchedDataLoading={isResourcesLoading}
                  pageSize={5}
                  dataModel={{
                    columns: [
                      {
                        header: 'Name',
                        render: (row: any) => row.name,
                        fullWidthMobile: true,
                        width: 25,
                      },
                      {
                        header: 'Type',
                        render: (row: any) =>
                          row.telegramGroupId
                            ? 'Telegram Group'
                            : row.telegramChannelId
                            ? 'Telegram Channel'
                            : 'Manual',
                        fullWidthMobile: true,
                      }
                    ],
                  }}
                />
              </div>
              <div className="mt-10 md:mt-14">
                <h2 className="h1 md:h3 mb-4">Members subscribed</h2>
                <BaseDynamicAPITable
                  apiMethod="/project/plan/members"
                  params={{
                    planId: plan.planId,
                    returnTable: true,
                  }}
                  pageSize={5}
                  dataModel={{
                    columns: [
                      {
                        header: 'Customer',
                        render: (row: any) => row.name,
                        fullWidthMobile: true,
                      },
                      {
                        header: 'Method',
                        render: (row: any) => row.method.charAt(0).toUpperCase() + row.method.slice(1),
                      },
                      {
                        header: 'Received',
                        render: (row: any) => moment(new Date(row.createdAt)).format('MMM D, h:mm A'),
                      },
                    ],
                  }}
                  rowLinkTo={(row) => '/user/' + row.userId}
                  rowLinkState={(row) => row}
                />
              </div>
              <EditPlanModal
                modalOpen={editPlanModalOpen}
                setModalOpen={setEditPlanModalOpen}
                plan={plan}
                submitCallback={onPlanUpdate}
              />
            </>
          )}
        </StaticLayoutInner>
      )}
      <NewPlanModal
        modalOpen={newPlanModalOpen}
        setModalOpen={setNewPlanModalOpen}
        submitCallback={onPlanCreate}
        plan={plan}
      />
      {plan && (
        <DeletePlanModal
          modalOpen={deletePlanModalOpen}
          setModalOpen={setDeletePlanModalOpen}
          plan={plan}
          onDelete={onDeletePlan}
          setIsLoading={setIsLoading}
          isLoading={isLoading}
        />
      )}
      <ErrorModal
        modalOpen={errorModalOpen}
        setModalOpen={setErrorModalOpen}
        error={errorModal}
      />
    </>
  );
}

export default SubscriptionPlan;
